import type * as React from 'react'
import { useState } from 'react'

import { LabeledSwatch } from '@/components/ui'
import { useChartBase } from '@/components/ui/chart'
import { numberToCompactCurrency } from '@/utils'
import type { ActivityMonth } from './caseActivity'

interface ChartReadyParams {
  containerRef: React.RefObject<HTMLDivElement>
  isFullscreen: boolean
  toggleFullscreen: () => Promise<void>
}

// Direction labels, so the same timeline reads as "Money in/out" or "Gains/Losses". Short forms keep the hover readout tight.
interface TimelineLabels {
  inflow: string
  outflow: string
  inflowShort: string
  outflowShort: string
  net: string
}

const DEFAULT_TIMELINE_LABELS: TimelineLabels = {
  inflow: 'Money in',
  outflow: 'Money out',
  inflowShort: 'In',
  outflowShort: 'Out',
  net: 'Net',
}

interface CaseActivityTimelineProps {
  months: ActivityMonth[]
  onChartReady?: (params: ChartReadyParams) => void
  labels?: TimelineLabels
}

// A run of this many empty months or more collapses into one labeled break, so a quiet stretch
// doesn't squeeze the months that matter. Shorter runs stay as real bars.
const GAP_THRESHOLD = 4

// Roughly how many month labels to print under the bars.
const TICK_TARGET = 6

// Smallest bar height, so a small but real month still registers next to a huge one.
const MIN_BAR_PX = 3

// Explicit colors (not Tailwind classes) so the SVG rasterizes correctly when exported to PNG.
const COLORS = {
  grid: '#e2e8f0',
  baseline: '#cbd5e1',
  gapLine: '#cbd5e1',
  gapText: '#64748b',
  gapTextStrong: '#475569',
  axisText: '#64748b',
  inflow: '#34d399',
  inflowActive: '#10b981',
  outflow: '#fb7185',
  outflowActive: '#f43f5e',
  columnHighlight: '#f1f5f9',
}

const MARGIN = { right: 16, top: 14, bottom: 22 }

// Money gridlines at full and half magnitude, mirrored above and below the baseline.
const AXIS_LEVELS = [1, 0.5]

type Segment =
  | { kind: 'month'; month: ActivityMonth; index: number }
  | { kind: 'gap'; count: number; key: string }

interface MonthMark {
  index: number
  month: ActivityMonth
  slotX: number
  slotWidth: number
  centerX: number
  barWidth: number
  inflowHeight: number
  outflowHeight: number
  labelled: boolean
}

interface GapMark {
  key: string
  count: number
  centerX: number
  // Inset dashed borders, derived from the gap's width so they never cross when it's narrow.
  lineLeftX: number
  lineRightX: number
}

interface Layout {
  monthMarks: MonthMark[]
  gapMarks: GapMark[]
  baselineY: number
  chartLeft: number
  chartRight: number
}

const isEmpty = (month: ActivityMonth): boolean =>
  month.inflow === 0 && month.outflow === 0

// Walk the months once, folding any run of GAP_THRESHOLD+ empty months into one gap segment.
const buildSegments = (months: ActivityMonth[]): Segment[] => {
  const segments: Segment[] = []
  let emptyRun: ActivityMonth[] = []

  const flushEmptyRun = () => {
    if (emptyRun.length >= GAP_THRESHOLD) {
      segments.push({
        kind: 'gap',
        count: emptyRun.length,
        key: `gap-${emptyRun[0].key}`,
      })
    } else {
      for (const month of emptyRun) {
        segments.push({ kind: 'month', month, index: months.indexOf(month) })
      }
    }
    emptyRun = []
  }

  months.forEach((month, index) => {
    if (isEmpty(month)) {
      emptyRun.push(month)
      return
    }
    flushEmptyRun()
    segments.push({ kind: 'month', month, index })
  })
  flushEmptyRun()

  return segments
}

// Anchors (the ends and the first month after each gap) always get a label. Samples are an even
// spacing in between; they only get drawn where they have room, so labels never stack.
interface LabelCandidates {
  anchors: Set<number>
  samples: Set<number>
}

const labelledMonthIndices = (
  segments: Segment[],
  monthCount: number,
): LabelCandidates => {
  const anchors = new Set<number>()
  const samples = new Set<number>()
  const every = Math.max(1, Math.ceil(monthCount / TICK_TARGET))
  let ordinal = 0
  segments.forEach((segment, segmentIndex) => {
    if (segment.kind !== 'month') return
    const afterGap =
      segmentIndex > 0 && segments[segmentIndex - 1].kind === 'gap'
    if (ordinal === 0 || ordinal === monthCount - 1 || afterGap) {
      anchors.add(segment.index)
    } else if (ordinal % every === 0) {
      samples.add(segment.index)
    }
    ordinal += 1
  })
  return { anchors, samples }
}

// Place each segment along the x axis: month slots share the leftover width, gaps take a fixed slice.
// Bars scale so the tallest month fills the half-height, keeping the chart full instead of squashed.
const computeLayout = (
  segments: Segment[],
  max: number,
  width: number,
  height: number,
  leftMargin: number,
  minGapWidth: number,
  gapPad: number,
  minLabelGap: number,
): Layout => {
  const chartLeft = leftMargin
  const chartRight = width - MARGIN.right
  const innerWidth = Math.max(0, chartRight - chartLeft)
  const innerHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom)
  const halfHeight = innerHeight / 2
  const baselineY = MARGIN.top + halfHeight
  const scaleMagnitude = (value: number) => (value / max) * halfHeight

  const monthCount = segments.filter(
    (segment) => segment.kind === 'month',
  ).length
  const gapCount = segments.length - monthCount
  // A gap must be wide enough to hold the "no activity" label between its borders, then scales gently with the canvas.
  let gapWidth = Math.max(minGapWidth, Math.min(160, innerWidth * 0.08))
  let slotWidth =
    monthCount > 0 ? (innerWidth - gapCount * gapWidth) / monthCount : 0
  if (slotWidth < 2 && gapCount > 0) {
    // Gaps ate too much room; shrink them so each slot keeps a sliver.
    gapWidth = Math.max(8, (innerWidth - 2 * monthCount) / gapCount)
    slotWidth = 2
  }
  // Dashed borders sit a fixed pad inside the gap edges, clamped so they can't cross when a gap is narrow.
  const gapInset = Math.min(gapPad, gapWidth / 2 - 1)

  const { anchors, samples } = labelledMonthIndices(segments, monthCount)
  const monthMarks: MonthMark[] = []
  const gapMarks: GapMark[] = []
  let cursorX = chartLeft

  for (const segment of segments) {
    if (segment.kind === 'gap') {
      gapMarks.push({
        key: segment.key,
        count: segment.count,
        centerX: cursorX + gapWidth / 2,
        lineLeftX: cursorX + gapInset,
        lineRightX: cursorX + gapWidth - gapInset,
      })
      cursorX += gapWidth
      continue
    }
    // Fill the slot so bars sit next to each other with only a hairline gap.
    const barWidth = Math.max(1, slotWidth - 2)
    monthMarks.push({
      index: segment.index,
      month: segment.month,
      slotX: cursorX,
      slotWidth,
      centerX: cursorX + slotWidth / 2,
      barWidth,
      inflowHeight:
        segment.month.inflow > 0
          ? Math.max(MIN_BAR_PX, scaleMagnitude(segment.month.inflow))
          : 0,
      outflowHeight:
        segment.month.outflow > 0
          ? Math.max(MIN_BAR_PX, scaleMagnitude(segment.month.outflow))
          : 0,
      labelled: false,
    })
    cursorX += slotWidth
  }

  // Keep every anchor, then add sampled labels only where they clear their neighbors,
  // so two labels never print on top of each other near the ends or a gap.
  const labelPositions: number[] = []
  const clearOfNeighbors = (centerX: number) =>
    labelPositions.every((placed) => Math.abs(placed - centerX) >= minLabelGap)
  for (const mark of monthMarks) {
    if (!anchors.has(mark.index)) continue
    mark.labelled = true
    labelPositions.push(mark.centerX)
  }
  for (const mark of monthMarks) {
    if (!samples.has(mark.index) || !clearOfNeighbors(mark.centerX)) continue
    mark.labelled = true
    labelPositions.push(mark.centerX)
  }

  return { monthMarks, gapMarks, baselineY, chartLeft, chartRight }
}

// A month-by-month pulse of money in (up, emerald) and out (down, rose) about a shared baseline. Long
// quiet stretches collapse into a labeled break so the active months stay readable. Rendered as SVG
// so the shared download/fullscreen controls work.
export const CaseActivityTimeline = ({
  months,
  onChartReady,
  labels = DEFAULT_TIMELINE_LABELS,
}: CaseActivityTimelineProps) => {
  const [hovered, setHovered] = useState<number | null>(null)
  const { containerRef, dimensions, isFullscreen } = useChartBase(onChartReady)

  const { width, height } = dimensions
  // Reserve a strip below the chart for the legend.
  const legendHeight = isFullscreen ? 34 : 24
  const svgHeight = Math.max(0, height - legendHeight)
  const ready = width > 0 && svgHeight > 0

  const max = Math.max(
    1,
    ...months.map((month) => Math.max(month.inflow, month.outflow)),
  )
  // Wide enough that money labels (e.g. "$152.5k") never clip, with extra room when fullscreen.
  const leftMargin = isFullscreen ? 76 : 56
  const fontSize = isFullscreen ? 13 : 10
  // Rough width of the widest month label plus a little breathing room, so adjacent axis
  // labels can't overlap (e.g. "Nov '23" next to the forced last label "Dec '23").
  const longestMonthLabel = months.reduce(
    (longest, month) => Math.max(longest, month.shortLabel.length),
    0,
  )
  const minLabelGap = longestMonthLabel * fontSize * 0.6 + 8
  const segments = buildSegments(months)

  // Size each gap from its widest label so the text fits between the dashed borders, with a fixed pad to the bars.
  const longestGapChars = segments.reduce(
    (longest, segment) =>
      segment.kind === 'gap'
        ? Math.max(longest, `${segment.count} mo`.length, 'no activity'.length)
        : longest,
    'no activity'.length,
  )
  // Padding scales with the canvas (like the bars), not snapping between fixed sizes.
  const gapPad = Math.min(30, Math.max(10, width * 0.014))
  const labelMargin = Math.min(18, Math.max(6, width * 0.009))
  const minGapWidth =
    longestGapChars * fontSize * 0.6 + 2 * labelMargin + 2 * gapPad

  const layout = ready
    ? computeLayout(
        segments,
        max,
        width,
        svgHeight,
        leftMargin,
        minGapWidth,
        gapPad,
        minLabelGap,
      )
    : null
  const halfHeight = layout ? layout.baselineY - MARGIN.top : 0
  // The two-line gap label needs more room when the chart is fullscreen.
  const gapLabelTop = isFullscreen ? 14 : 5
  const gapLabelBottom = isFullscreen ? 28 : 14
  const active =
    layout?.monthMarks.find((mark) => mark.index === hovered) ?? null
  const tooltipTransform = !active
    ? 'translateX(-50%)'
    : active.centerX < width * 0.15
      ? 'translateX(0)'
      : active.centerX > width * 0.85
        ? 'translateX(-100%)'
        : 'translateX(-50%)'

  return (
    <div
      ref={containerRef}
      className={`group relative h-full w-full ${isFullscreen ? 'bg-white p-4' : ''}`}
    >
      {layout ? (
        <svg
          role="img"
          aria-label="Account activity by month"
          width={width}
          height={svgHeight}
          viewBox={`0 0 ${width} ${svgHeight}`}
          onMouseLeave={() => setHovered(null)}
        >
          {AXIS_LEVELS.map((level) => {
            const topY = layout.baselineY - level * halfHeight
            const bottomY = layout.baselineY + level * halfHeight
            const label = numberToCompactCurrency(max * level)
            return (
              <g key={`level-${level}`}>
                <line
                  x1={layout.chartLeft}
                  x2={layout.chartRight}
                  y1={topY}
                  y2={topY}
                  stroke={COLORS.grid}
                />
                <line
                  x1={layout.chartLeft}
                  x2={layout.chartRight}
                  y1={bottomY}
                  y2={bottomY}
                  stroke={COLORS.grid}
                />
                <text
                  x={layout.chartLeft - 8}
                  y={topY}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={fontSize}
                  fill={COLORS.axisText}
                >
                  {label}
                </text>
                <text
                  x={layout.chartLeft - 8}
                  y={bottomY}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={fontSize}
                  fill={COLORS.axisText}
                >
                  {label}
                </text>
              </g>
            )
          })}

          <line
            x1={layout.chartLeft}
            x2={layout.chartRight}
            y1={layout.baselineY}
            y2={layout.baselineY}
            stroke={COLORS.baseline}
          />
          <text
            x={layout.chartLeft - 8}
            y={layout.baselineY}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={fontSize}
            fill={COLORS.axisText}
          >
            $0
          </text>

          {layout.gapMarks.map((gap) => (
            <g key={gap.key}>
              <line
                x1={gap.lineLeftX}
                x2={gap.lineLeftX}
                y1={MARGIN.top + 6}
                y2={svgHeight - MARGIN.bottom - 6}
                stroke={COLORS.gapLine}
                strokeDasharray="3 3"
              />
              <line
                x1={gap.lineRightX}
                x2={gap.lineRightX}
                y1={MARGIN.top + 6}
                y2={svgHeight - MARGIN.bottom - 6}
                stroke={COLORS.gapLine}
                strokeDasharray="3 3"
              />
              <text
                x={gap.centerX}
                y={layout.baselineY - gapLabelTop}
                textAnchor="middle"
                fontSize={fontSize}
                fontWeight={600}
                fill={COLORS.gapTextStrong}
              >
                {gap.count} mo
              </text>
              <text
                x={gap.centerX}
                y={layout.baselineY + gapLabelBottom}
                textAnchor="middle"
                fontSize={fontSize}
                fill={COLORS.gapText}
              >
                no activity
              </text>
            </g>
          ))}

          {layout.monthMarks.map((mark) => {
            const isActive = mark.index === hovered
            return (
              <g key={mark.month.key}>
                {isActive ? (
                  <rect
                    x={mark.slotX}
                    y={MARGIN.top}
                    width={mark.slotWidth}
                    height={svgHeight - MARGIN.top - MARGIN.bottom}
                    fill={COLORS.columnHighlight}
                  />
                ) : null}
                {mark.inflowHeight > 0 ? (
                  <rect
                    x={mark.centerX - mark.barWidth / 2}
                    y={layout.baselineY - mark.inflowHeight}
                    width={mark.barWidth}
                    height={mark.inflowHeight}
                    rx={2}
                    fill={isActive ? COLORS.inflowActive : COLORS.inflow}
                  />
                ) : null}
                {mark.outflowHeight > 0 ? (
                  <rect
                    x={mark.centerX - mark.barWidth / 2}
                    y={layout.baselineY}
                    width={mark.barWidth}
                    height={mark.outflowHeight}
                    rx={2}
                    fill={isActive ? COLORS.outflowActive : COLORS.outflow}
                  />
                ) : null}
                {mark.labelled ? (
                  <text
                    x={mark.centerX}
                    y={svgHeight - 8}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fill={COLORS.monthText}
                  >
                    {mark.month.shortLabel}
                  </text>
                ) : null}
                <rect
                  x={mark.slotX}
                  y={MARGIN.top}
                  width={mark.slotWidth}
                  height={svgHeight - MARGIN.top - MARGIN.bottom}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHovered(mark.index)}
                />
              </g>
            )
          })}
        </svg>
      ) : null}

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-4 text-xs text-blue-5"
        style={{ height: legendHeight }}
      >
        <LabeledSwatch
          shape="square"
          color={COLORS.inflow}
          label={labels.inflow}
        />
        <LabeledSwatch
          shape="square"
          color={COLORS.outflow}
          label={labels.outflow}
        />
      </div>

      {active ? (
        <div
          className="pointer-events-none absolute z-20 whitespace-nowrap rounded-md border bg-white px-2.5 py-1 text-xs shadow-sm"
          style={{
            left: `${active.centerX}px`,
            top: MARGIN.top,
            transform: tooltipTransform,
          }}
        >
          <span className="font-medium text-blue-7">{active.month.label}</span>
          <span className="ml-2 tabular-nums text-emerald-600">
            {labels.inflowShort} {numberToCompactCurrency(active.month.inflow)}
          </span>
          <span className="ml-2 tabular-nums text-red-600">
            {labels.outflowShort}{' '}
            {numberToCompactCurrency(active.month.outflow)}
          </span>
          <span className="ml-2 tabular-nums text-blue-5">
            {labels.net}{' '}
            {numberToCompactCurrency(
              active.month.inflow - active.month.outflow,
            )}
          </span>
        </div>
      ) : null}
    </div>
  )
}
